// One set of timer dialogs and one stop flow for the hero, the top-bar indicator, the quick-start
// card and the check-in controller (architecture sprint-04 §5.4, F41–F46). Mounted once in AppShell.
import { useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useMe } from '@/hooks/use-me';
import { useActiveShift } from '@/hooks/use-shifts';
import {
  TIMER_QUERY_KEY,
  useDiscardTimer,
  usePauseTimer,
  useResumeTimer,
  useStartTimer,
  useStopTimer,
  useSwitchTimer,
  useTimer,
} from '@/hooks/use-timer';
import { formatDurationHm } from '@/lib/format';
import { handleGlobalError } from '@/lib/query-client';
import type { StartBody, StopResult, SwitchBody, SwitchResult } from '@/types/timer';
import { MidnightDialog } from './MidnightDialog';
import { StartWithDetailsDialog } from './StartWithDetailsDialog';
import { SwitchDialog } from './SwitchDialog';
import { ON_SHIFT_MESSAGE, TIMER_SWITCH_DISABLED_MESSAGE, timerErrorMessage } from './timer-errors';

export interface TimerActions {
  startInstant: () => void;
  startWith: (body: { projectId: string | null; description: string | null }) => void;
  /** Sprint 15 (§9.3): an optional project pre-fill (the dashboard selection). */
  openStartWithDetails: (prefill?: { projectId: string | null }) => void;
  pause: () => void;
  resume: () => void;
  openSwitch: () => void;
  requestStop: () => void;
  requestDiscard: () => void;
  /**
   * Sprint 13: stop the timer through the one stop flow (the midnight dialog may appear), then run next()
   * once it saved or saved nothing. Cancelling the midnight dialog or an error drops it; no timer runs it at once.
   */
  stopThen: (next: () => void) => void;
  /** Sprint 13: an open shift exists; starting a timer is refused (§8). */
  onShift: boolean;
  /** Any timer mutation in flight: controls are disabled. */
  pending: boolean;
}

export type InlineErrorSink = (message: string) => void;

const TimerActionsContext = createContext<TimerActions | null>(null);

type DecisionRequired = Extract<StopResult, { outcome: 'decision_required' }>;

function savedToast(result: StopResult, midnight: 'split' | 'keep' | undefined): void {
  if (result.outcome === 'nothing_saved') {
    toast('Under 1 minute — nothing saved');
    return;
  }
  if (result.outcome !== 'saved') return;
  if (midnight === 'keep' && result.droppedSeconds > 0) {
    toast.success('Saved, capped at 24h');
    return;
  }
  const seconds = result.entries.reduce((acc, e) => acc + e.durationSeconds, 0);
  toast.success(`Saved ${formatDurationHm(seconds)}`);
}

function switchedToast(result: SwitchResult): void {
  const seconds = result.savedEntries.reduce((acc, e) => acc + e.durationSeconds, 0);
  toast.success(seconds > 0 ? `Switched. Saved ${formatDurationHm(seconds)}` : 'Switched');
}

export function TimerActionsProvider({ children }: { children: ReactNode }): JSX.Element {
  const queryClient = useQueryClient();
  const { data: snapshot } = useTimer();
  const { data: me } = useMe();
  const start = useStartTimer();
  const pauseMutation = usePauseTimer();
  const resumeMutation = useResumeTimer();
  const switchMutation = useSwitchTimer();
  const stop = useStopTimer();
  const discard = useDiscardTimer();
  const onShift = Boolean(useActiveShift()?.shift);
  const afterStopRef = useRef<(() => void) | null>(null);

  const [startDetailsOpen, setStartDetailsOpen] = useState(false);
  const [startPrefill, setStartPrefill] = useState<{ projectId: string | null } | undefined>(undefined);
  const [switchOpen, setSwitchOpen] = useState(false);
  const [decision, setDecision] = useState<DecisionRequired | null>(null);
  const [discardOpen, setDiscardOpen] = useState(false);

  const closeAll = useCallback((): void => {
    setStartDetailsOpen(false);
    setSwitchOpen(false);
    setDecision(null);
    setDiscardOpen(false);
  }, []);

  /** §5.7: one place that turns a timer error into a toast, an inline message or a redirect. */
  const handleError = useCallback(
    (err: unknown, inline?: InlineErrorSink, oneClickStart = false): void => {
      const mapped = timerErrorMessage(err);
      if (mapped.global) {
        handleGlobalError(err, undefined);
        return;
      }
      if (mapped.refetch) void queryClient.invalidateQueries({ queryKey: TIMER_QUERY_KEY });
      if (mapped.projectArchived && oneClickStart) {
        void queryClient.invalidateQueries({ queryKey: ['projects'] });
        void queryClient.invalidateQueries({ queryKey: ['me'] });
        void queryClient.invalidateQueries({ queryKey: ['entries', 'quick-start'] });
      }
      if (mapped.closeDialog) {
        closeAll();
        toast.error(mapped.message);
        return;
      }
      if (mapped.inline && inline) inline(mapped.message);
      else toast.error(mapped.message);
    },
    [queryClient, closeAll],
  );

  const version = snapshot?.timer.version;

  const runStop = useCallback(
    (v: number, midnight?: 'split' | 'keep'): void => {
      stop.mutate(midnight ? { version: v, midnight } : { version: v }, {
        onSuccess: (result) => {
          if (result.outcome === 'decision_required') {
            setDecision(result);
            return;
          }
          setDecision(null);
          savedToast(result, midnight);
          const next = afterStopRef.current;
          afterStopRef.current = null;
          next?.();
        },
        onError: (err) => {
          afterStopRef.current = null;
          handleError(err);
        },
      });
    },
    [stop, handleError],
  );

  const startOneClick = useCallback(
    (body: StartBody): void => {
      start.mutate(body, { onError: (err) => handleError(err, undefined, true) });
    },
    [start, handleError],
  );

  const actions = useMemo<TimerActions>(
    () => ({
      startInstant: () => (onShift ? toast.error(ON_SHIFT_MESSAGE) : startOneClick({})),
      startWith: (body) => (onShift ? toast.error(ON_SHIFT_MESSAGE) : startOneClick({ projectId: body.projectId, description: body.description })),
      openStartWithDetails: (prefill) => {
        if (onShift) {
          toast.error(ON_SHIFT_MESSAGE);
          return;
        }
        setStartPrefill(prefill);
        setStartDetailsOpen(true);
      },
      pause: () => {
        if (version !== undefined) pauseMutation.mutate(version, { onError: (err) => handleError(err) });
      },
      resume: () => {
        if (version !== undefined) resumeMutation.mutate(version, { onError: (err) => handleError(err) });
      },
      openSwitch: () => {
        if (!snapshot) return;
        if (snapshot.timer.pausedAt !== null) {
          toast('Resume the timer before switching');
          return;
        }
        // Sprint 11 (WP-10): the API refuses it too (TIMER_SWITCH_DISABLED).
        if (snapshot.timer.rules?.switchAllowed === false) {
          toast(TIMER_SWITCH_DISABLED_MESSAGE);
          return;
        }
        setSwitchOpen(true);
      },
      requestStop: () => {
        if (version !== undefined) runStop(version);
      },
      requestDiscard: () => {
        if (snapshot) setDiscardOpen(true);
      },
      stopThen: (next) => {
        if (version === undefined) {
          next();
          return;
        }
        afterStopRef.current = next;
        runStop(version);
      },
      onShift,
      pending:
        start.isPending || pauseMutation.isPending || resumeMutation.isPending || switchMutation.isPending || stop.isPending || discard.isPending,
    }),
    [startOneClick, version, snapshot, pauseMutation, resumeMutation, switchMutation, stop, discard, start, runStop, handleError, onShift],
  );

  function submitStart(body: StartBody, inline: InlineErrorSink): void {
    start.mutate(body, {
      onSuccess: () => setStartDetailsOpen(false),
      onError: (err) => handleError(err, inline),
    });
  }

  function submitSwitch(body: SwitchBody, inline: InlineErrorSink): void {
    if (version === undefined) return;
    switchMutation.mutate(
      { ...body, version },
      {
        onSuccess: (result) => {
          setSwitchOpen(false);
          switchedToast(result);
        },
        onError: (err) => handleError(err, inline),
      },
    );
  }

  function confirmDiscard(): void {
    if (version === undefined) return;
    discard.mutate(version, {
      onSuccess: () => {
        setDiscardOpen(false);
        toast('Timer discarded');
      },
      onError: (err) => {
        setDiscardOpen(false);
        handleError(err);
      },
    });
  }

  return (
    <TimerActionsContext.Provider value={actions}>
      {children}
      {me && (
        <StartWithDetailsDialog
          open={startDetailsOpen}
          onOpenChange={setStartDetailsOpen}
          tz={me.timezone}
          pending={start.isPending}
          onSubmit={submitStart}
          {...(startPrefill ? { initialProjectId: startPrefill.projectId } : {})}
        />
      )}
      {me && snapshot && (
        <SwitchDialog
          open={switchOpen}
          onOpenChange={setSwitchOpen}
          snapshot={snapshot}
          me={me}
          pending={switchMutation.isPending}
          onSubmit={submitSwitch}
        />
      )}
      <MidnightDialog
        decision={decision}
        pending={stop.isPending}
        onChoose={(midnight) => {
          if (decision) runStop(decision.timer.version, midnight);
        }}
        onCancel={() => {
          // Cancelling the midnight choice aborts a chained shift start too.
          afterStopRef.current = null;
          setDecision(null);
        }}
      />
      <ConfirmDialog
        open={discardOpen}
        onOpenChange={setDiscardOpen}
        title="Discard this timer?"
        body="Nothing will be saved."
        confirmLabel="Discard"
        destructive
        pending={discard.isPending}
        onConfirm={confirmDiscard}
      />
    </TimerActionsContext.Provider>
  );
}

export function useTimerActions(): TimerActions {
  const ctx = useContext(TimerActionsContext);
  if (!ctx) throw new Error('useTimerActions must be used inside <TimerActionsProvider>');
  return ctx;
}
